import { gql } from '@apollo/client'
import { buildGraphqlQuery } from '@ztechapps/common.utils'
import { checkoutGraphql } from '@ztechapps/order.webui'
import { conversationFragment as conversationFragmentCore } from '@ztechapps/chat.webui'
import { orderFragment as orderFragmentCore } from '@ztechapps/order.common'

export const productMetadataFragment = `
 metadata {
    _id
    key
    value
    unit
 }

`

export const productFragment = `
 product {
      _id
      code
      name
      coverImage
      status
      inventory {
        _id
        quantityOnHand
      }
      inStock
       requiredComputedQuantityOnHand
       hasSubComponents
       productType
       requiredSerialNumber
       orderConversionFactor
       unitOfMeasurePrecision
       ${productMetadataFragment}
    }
`

export const conversationFragment = conversationFragmentCore

//we need to use the query from checkoutGraphql, so it will be sync with the CheckoutProvider
//This is related to the issue https://gitlab.com/ztech-apps/ztech-apps/-/issues/866
export const paymentFragment = checkoutGraphql.paymentFragment

export const shippingFragment = `shipping {
    _id
    serviceErrors
    serviceWarnings
    voidMessage
    status
    shipmentId
    amount
    shippingCost
    insuranceCost
    confirmationCost
    markupCost
    otherCost
    date
    trackingNumber
    trackingUrl
    pdfLabel
    pngLabel
    zplLabel
    pickupId
    pickupWindowStartDate
    pickupWindowEndDate
    pickupNotes
    pickupCancelledDate
    isVoidLabel
    voidMessage
    rate {
      _id
      deliveryDays
      estimatedDeliveryDate
      serviceType
      carrierName
      serviceCode
      carrierId
    }
    labelId
    billToCustomer
    billToAccount
    billToPostalCode
    billToCountryCode
    serviceErrors
    deliveryConfirmation
    insuredAmount
    packages  {
      _id
      weight {
       unit
       value
      }
      dimensions{
        unit
        length
        width
        height
      }
      insured_value{
        currency
        amount
      }
      label_download {
        png
        pdf
      }
      tracking_number
      package_code
      trackingUrl
      packageType
    }
    serviceWarnings
    formDownload
    hrefLabel
    itemQuantityErrors
  }`

export const shippingAddressFragment = `shippingAddress{
      _id
      street1
      street2
      city
      state
      zip
      country
      fullAddress
      company
      street3
      name
      phone
    }`

export const billingAddressFragment = `billingAddress{
      _id
      street1
      street2
      city
      state
      zip
      country
      fullAddress
      company
      street3
      name
      phone
    }
    taxAmount`

export const customerFragment = `_id
    profile {
      _id
      name
      picture
      email
    }
    status
    company {
      _id
      name
      domain
    }
    phone
    address {
        _id
       street1
       street2
       street3
       city
       state
       zip
       fullAddress
       country
    }
    addresses {
        _id
       street1
       street2
       street3
       city
       state
       zip
       fullAddress
       country
       status
    }
    ups {
      account
      postalCode
      countryCode
    }

    `
export const paymentBalanceFragment = checkoutGraphql.paymentBalanceFragment

export const relatedOrdersFragment = `
relatedOrders {
    _id
    code
    status
    paymentStatus
    summary
    customerPoNumber
    relatedOrders {
      _id
    }
    by {
      _id
      company {
        _id
        name
      }
      profile {
        _id
        name
        picture
        email
      }
    }
   }
`

export const assestsFragment = `
assets {
    _id
    url
    title
    subtitle
    type
  }`
//export const membersFragment = checkoutGraphql.membersFragment
export const membersFragment = `members {
    _id
    profile {
      _id
      name
      email
      picture
    }
    company {
      _id
      name
    }
    phone
    remarks
    tags
    address {
      _id
      fullAddress
    }
  }`

export const remarksActivityFragment = `remarksActivity {
  _id
  activity
  by {
    _id
    profile {
      _id
      name
      picture
    }
  }
  updatedAt
  isDeleted
}`

//TODO: Deprecate this, and use { orderFragment } from '@ztechapps/order.common'
export const orderFragment = `_id
  summary
  code
  status
  updatedAt
  createdAt
  net
  by {
    ${customerFragment}
  }
  items {
    _id
    ${productFragment}
    quantity
    price
    net
    remarks
    tags
    discount
    originalPrice
    shippingWeight
    serialNumbers {
      _id
      code
    }
    uom {
      _id
      code
      name
      symbol
    }
  }
  shippingFee
  gross
  remarks
  tags
  taxAmount
  discount
  discountPercent
  ${paymentFragment}
  ${conversationFragment}
  ${shippingFragment}
  ${shippingAddressFragment}
  ${billingAddressFragment}
  customerPoNumber
  isManualShippingRate
  allItemsInStock
  totalWeight
  allowPrePayment
  minimumOrderAmount
  invoiceNotes
  ${membersFragment}
  isManualTaxAmount
  manualTrackingUrl
  manualTrackingNumber
  manualShippingCarrier
  paymentStatus
  initialShippingFee
  paymentTerms
  checkoutStage
  ${paymentBalanceFragment}

  qboId
  ${relatedOrdersFragment}
  ${assestsFragment}
  ${remarksActivityFragment}
`

export const totalAmountFragment = `isManualShippingRate shippingFee gross net taxAmount discountPercent discount discountPercent ${shippingFragment} ${paymentBalanceFragment} ${paymentFragment}`

// Admin order-detail document: the shared order fragment plus the channel-neutral conversation id the
// Communication tab mounts against. Kept local (not on the shared orderFragment) so the storefront cart /
// checkout / list queries that also use orderFragment don't trigger the per-order chat lookup the
// activeConversationId field resolver does on the Email channel.
const orderDetailFragment = gql`
  fragment orderDetailFragment on Order {
    ...orderFragment
    activeConversationId
  }
  ${orderFragmentCore}
`

export const queries = buildGraphqlQuery('Order', orderDetailFragment, ['payments'])

export const buildQuery = (field: string) => buildGraphqlQuery('Order', `_id updatedAt status paymentStatus ${field}`)

export const buildOrderRemarksItemQuery = buildGraphqlQuery(
  'Order',
  `_id status paymentStatus items { _id remarks serialNumbers {_id code}  }`,
  ['items']
)

export const orderAcceptQuery = gql`
  mutation orderAccept($input: JSON!) {
    orderAccept(input: $input) {
      _id
      status
    }
  }
`

export const orderCancelQuery = gql`
  mutation orderCancel($input: JSON!) {
    orderCancel(input: $input) {
        _id
      ${orderFragment}
    }
  }
`

export const orderMarkAsReadyForPaymentQuery = gql`
  mutation orderMarkAsReadyForPayment($input: JSON!) {
    orderMarkAsReadyForPayment(input: $input) {
      _id
      status
    }
  }
`

const productInventoryFragment = gql`
  fragment productInventoryFragment on Product {
    _id
    inventory {
      _id
      quantityOnHand
    }
  }
`

export const productQuery = buildGraphqlQuery('Product', productInventoryFragment)

export const orderCloneQuery = gql`
  mutation cloneOrder($input: CloneOrderArgs!) {
    cloneOrder(input: $input) {
        _id
      ${orderFragment}
    }
  }
`

export const deleteQuery = buildGraphqlQuery('Order', '_id')

export const customerQuery = buildGraphqlQuery('Customer', customerFragment, ['addresses'])

export const estimateShippingFeeQuery = gql(`
  mutation orderEstimateShippingFee($input: JSON!) {
    orderEstimateShippingFee(input: $input) {
      _id
      shippingFee
      isManualShippingRate
    }
  }
`)

export const orderFinalizeQuery = gql`
  mutation orderFinalize($input: FinalizeOrderServiceArgs!) {
    orderFinalize(input: $input) {
        _id
      ${orderFragment}
    }
  }
`

export const orderPayQuery = gql`
  mutation orderPay($input: JSON) {
    orderPay(input: $input) {
        _id
     ${orderFragment}
    }
  }
`

export const ESTIMATE_ORDER_SHIPPING_FEE_WITH_PACKAGES_AND_INSURANCE_QUERY = gql`
  ${orderFragmentCore}
  mutation estimateOrderShippingFeeWithPackagesAndInsurance($input: ShipOrderArgs!) {
    estimateOrderShippingFeeWithPackagesAndInsurance(input: $input) {
      ...orderFragment
    }
  }
`
